import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import FullScreenModal from 'Components/Atoms/FullScreenModal';
import useToast from 'CustomHooks/useToast';
import { Severity } from 'Contexts/RestaurantContext';
import { Event } from 'types/shared/documents/restaurants';
import { EventTypes } from 'types/shared/documents/restaurants/event';
import { generateEventId, normalizeEventId } from 'utils/normalizeEventId';
import EventDetails from './Components/EventDetails';
import useRestaurantVatCountry from 'CustomHooks/useRestaurantVatCountry';
import { eventVatSaveError } from './Components/vatSplitSaveError';

export function createDefaultEvent(): Event {
  return {
    id: generateEventId(),
    active: false,
    amount: 0,
    title: { de: '', en: '' },
    description: { de: '', en: '' },
    type: EventTypes.DINNER_TICKET,
    blockTableFor: {
      value: 1,
      unit: 'hours',
    },
    minInAdvance: {
      value: 3,
      unit: 'hours',
    },
    options: [
      {
        id: 'default',
        vatRate: '7.00',
        internalName: 'default-opt',
        translation: {
          de: { title: 'Default option', description: '' },
          en: { title: '', description: '' },
        },
        type: 'perPerson',
      },
    ],
    confirmation_email: {
      active: false,
      translations: {
        en: { title: '', description: '', button: '' },
        de: { title: '', description: '', button: '' },
      },
    },
    reminder_email: {
      active: false,
      sendBefore: {
        value: 3,
        unit: 'hours',
      },
      translations: {
        en: { title: '', description: '', button: '' },
        de: { title: '', description: '', button: '' },
      },
    },
  };
}

function prepareEvent(event: Event): Event {
  return normalizeEventId({
    ...event,
    options: event.options?.filter((option) => option.id !== 'newOpt') ?? [],
  });
}

type EventSettingsDialogProps = {
  open: boolean;
  title?: string;
  eventState: Event | null;
  setEventState: React.Dispatch<React.SetStateAction<Event | null>>;
  onClose: VoidFunction;
  onSubmit: (event: Event) => void | Promise<void>;
  header?: React.ReactNode;
  showShiftActivation?: boolean;
  /**
   * Stored configuration the edit is measured against, set when a saved
   * template is selected (`null`: a new event). Not given: the event as opened.
   */
  authoringBaseline?: Event | null;
};

const EventSettingsDialog = ({
  open,
  title = 'Event Settings',
  eventState,
  setEventState,
  onClose,
  onSubmit,
  header,
  showShiftActivation = true,
  authoringBaseline,
}: EventSettingsDialogProps) => {
  const toast = useToast();
  const { t } = useTranslation('calendar');
  const vatCountry = useRestaurantVatCountry();
  // The configuration as opened: unchanged historical single rates stay saveable.
  const openedEvent = useRef<Event | null>(null);
  useEffect(() => {
    if (!open) openedEvent.current = null;
    else if (eventState && !openedEvent.current) openedEvent.current = eventState;
  }, [open, eventState]);

  const handleSubmit = () => {
    if (!eventState?.title?.de?.trim()) {
      toast(
        'A title in the German tab is required, please provide one to save.',
        Severity.WARNING,
        'calendar'
      );
      return;
    }

    const event = prepareEvent(eventState);

    if (event.type === EventTypes.DINNER_TICKET && !event.options?.length) {
      toast(
        'At least one option is needed to save.',
        Severity.WARNING,
        'calendar'
      );
      return;
    }

    // A changed event base price changes every option's full price; a split
    // that no longer adds up must be redistributed on purpose first.
    const previous = authoringBaseline !== undefined ? authoringBaseline : openedEvent.current;
    const vatError = eventVatSaveError(event, vatCountry.country, previous, t);
    if (vatError) {
      // Already translated (it may contain an option name such as "VIP_food"): no second lookup.
      toast(vatError, Severity.WARNING, null);
      return;
    }

    void onSubmit(event);
  };

  return (
    <FullScreenModal
      open={open}
      onClose={onClose}
      title={title}
      titleTranslation="calendar"
      onSubmit={handleSubmit}
      fixedNavBar
    >
      <Box style={{ width: 960, maxWidth: '100%', margin: '0 auto' }}>
        {header}
      </Box>
      <EventDetails
        eventState={eventState}
        setEventState={setEventState}
        showShiftActivation={showShiftActivation}
      />
    </FullScreenModal>
  );
};

export default EventSettingsDialog;
